import { Box, IconButton, Tooltip } from "@mui/material";
import {
  DataGrid,
  type GridColDef,
  type GridPaginationModel,
  type GridRowParams,
} from "@mui/x-data-grid";
import { RootState } from "../../../../../store/Index";
import { useSelector } from "react-redux";
import { NoAssignees } from "./StreamTableRow";
import { useNavigate } from "react-router-dom";
import { User } from "../../../../users-admin/types/User";
import { TableRowCommentsCounts } from "./TableRowCommentsCount";
import { LastUpdatedByAvatar } from "./LastUpdatedByAvatar";
import { TableRowAssignees } from "./TableRowAssignees";
import { CanvaLinkButton } from "./CanvaLinkButton";
import { MemoryBookStatusChip } from "./MemoryBookStatusChip";
import { useState } from "react";
import { DownloadIcon } from "lucide-react";
import { DownloadGraphicModal } from "./DownloadGraphicModal";
import memorygraphicpattern from "../../../../../assets/thankyoupattern.png";
import muzukurulogo from "../../../../../assets/muzukurublack.png";
import PropTypes from "prop-types";
import { TableRowScannedComments } from "./TableRowScannedComments";

interface MemoryBookStreamInfo {
  is_delivered: boolean;
  is_memory_book_available: boolean;
  approved_comments: number;
  total_comments: number;
  canva_memory_book_link: string | null;
  assignees: User[];
  created_at: string;
  last_updated_by: User;
  name_of_deceased: string;
  stream_date: string;
  stream_id: number;
  updated_at: string;
  physical_comments?: number;
  done_comments?: number;
  due_date?: string;
}

interface Props {
  tableViewStreams: MemoryBookStreamInfo[];
  isLoading: boolean;
  paginationModel: GridPaginationModel;
  rowCount: number;
  handlePaginationModelChange: (newModel: GridPaginationModel) => void;
}

export const TableView: React.FC<Props> = ({
  tableViewStreams,
  isLoading,
  paginationModel,
  rowCount,
  handlePaginationModelChange,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const handleRowClick = (params: GridRowParams<MemoryBookStreamInfo>) => {
    navigate(`/memory-table/view/${params.row.stream_id}`);
  };

  const [selectedDeceased, setSelectedDeceased] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const handleOpenModal = (name: string) => {
    setSelectedDeceased(name);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedDeceased(null);
  };

  const getDate = (date: string): string => {
    const dateObj = new Date(date);

    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();

    const formattedDate = `${day}/${month}/${year}`;
    return formattedDate;
  };

  const columns: GridColDef<MemoryBookStreamInfo>[] = [
    { field: "stream_id", headerName: "Stream ID", width: 120 },
    {
      field: "stream_date",
      headerName: "Date of Stream",
      flex: 1,
      renderCell: (params) => {
        return getDate(params.row.stream_date);
      },
    },
    {
      field: "name_of_deceased",
      headerName: "Name of Deceased",
      flex: 1,
    },
    {
      field: "comments",
      headerName: "Comments",
      flex: 1,
      renderCell: (params) => {
        const { approved_comments, total_comments } = params.row;
        return (
          <TableRowCommentsCounts
            approvedComments={approved_comments}
            allComments={total_comments}
          />
        );
      },
    },
    {
      field: "scanned_comments",
      headerName: "Scanned Comments",
      flex: 1,
      renderCell: (params) => {
        const doneComments = params.row.done_comments || 0;
        const physicalComments = params.row.physical_comments || 0;
        
        return (
          <TableRowScannedComments
            doneComments={doneComments}
            physicalComments={physicalComments}
          />
        );
      },
    },
    {
      field: "book_status",
      headerName: "Book Status",
      flex: 1,
      renderCell: (params) => {
        return (
          <MemoryBookStatusChip
            deliveredStatus={params.row.is_delivered}
            isBookAvailable={params.row.is_memory_book_available}
          />
        );
      },
    },
    {
      field: "canva_link",
      headerName: "Canva Link",
      flex: 1,
      renderCell: (params) => {
        return (
          <CanvaLinkButton canvaLink={params.row.canva_memory_book_link} />
        );
      },
    },
    {
      field: "last_updated_by",
      headerName: "Last Updated by",
      flex: 1,
      renderCell: (params) => {
        const isMemoryBookAvailable = params.row.is_memory_book_available;
        const lastUpdatedBy = params.row.last_updated_by;

        const determinLastUpdateName = () => {
          if (!isMemoryBookAvailable) {
            return "Not available";
          }

          if (isMemoryBookAvailable && !lastUpdatedBy) {
            return "Not updated yet";
          }

          if (lastUpdatedBy.first_name && lastUpdatedBy.last_name) {
            return `${lastUpdatedBy.first_name.charAt(0)} ${
              lastUpdatedBy.last_name
            }`;
          }

          return lastUpdatedBy.username;
        };

        return <LastUpdatedByAvatar name={determinLastUpdateName()} />;
      },
    },
    {
      field: "assignee",
      headerName: "Assignee",
      flex: 1,
      renderCell: (params) => {
        const assignees = params.row.assignees;

        if (assignees.length === 0) {
          return <NoAssignees size={25} />;
        }

        return <TableRowAssignees assignees={assignees} />;
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Download Thank you graphic">
            <IconButton
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(params.row.name_of_deceased);
              }}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box
      sx={{
        bgcolor: reduxTheme.primary_colors[900],
        padding: 1,
        borderRadius: 2,
      }}
    >
      <DataGrid
        rows={tableViewStreams}
        getRowId={(row) => row.stream_id}
        columns={columns}
        loading={isLoading}
        rowCount={rowCount}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        onRowClick={handleRowClick}
        sx={{
          zIndex: 1,
          "& .MuiDataGrid-row": {
            backgroundColor: "#fff", // light gray
          },
          cursor: "pointer",
        }}
      />

      <DownloadGraphicModal
        open={openModal}
        onClose={handleCloseModal}
        deceasedName={selectedDeceased || ""}
        backgroundImage={memorygraphicpattern} // your background pattern
        logoImage={muzukurulogo} // your logo
      />
    </Box>
  );
};

TableView.propTypes = {
  tableViewStreams: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool.isRequired,
  paginationModel: PropTypes.shape({
    page: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
  }).isRequired,
  rowCount: PropTypes.number.isRequired,
  handlePaginationModelChange: PropTypes.func.isRequired,
};
